import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { Lightning, CircleNotch, MagnifyingGlass } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PrimaryButton, GhostButton } from "@/components/admin/parts";
import { adminApi, getErrorMessage } from "@/lib/api";
import { RUN_FIELDS, runFieldLabel } from "./ruleText";

const MODES = [["fill_blanks", "Fill blank fields only", "Nothing already filled in is changed."], ["correct", "Fill blanks and correct different values", "Also replaces values that don't match the rules."]];

function Options({ mode, setMode, fields, setFields }) {
  const toggle = (k) => setFields(fields.includes(k) ? fields.filter((x) => x !== k) : [...fields, k]);
  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-2">
        {MODES.map(([k, l, h]) => (
          <button key={k} onClick={() => setMode(k)} data-testid={`run-all-mode-${k}`} className={`text-left rounded-xl border px-4 py-3 transition-colors ${mode === k ? "border-[#73bf44] bg-[#73bf44]/10" : "border-neutral-800 hover:border-neutral-600"}`}>
            <div className="text-sm text-white font-medium">{l}</div><div className="text-xs text-neutral-500 mt-0.5">{h}</div>
          </button>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        {RUN_FIELDS.map(([k, l]) => (
          <label key={k} className={`inline-flex items-center gap-2 text-xs rounded-full border px-3 py-1.5 cursor-pointer ${fields.includes(k) ? "border-[#73bf44]/50 text-white" : "border-neutral-800 text-neutral-500"}`}>
            <input type="checkbox" checked={fields.includes(k)} onChange={() => toggle(k)} className="accent-[#73bf44]" data-testid={`run-all-field-${k}`} /> {l}
          </label>
        ))}
      </div>
      <p className="text-xs text-neutral-500">Hubs that customers picked themselves are never changed.</p>
    </div>
  );
}

function Changes({ res, exclude, setExclude }) {
  const [q, setQ] = useState("");
  const [field, setField] = useState("");
  const [limit, setLimit] = useState(200);
  const rows = useMemo(() => res.rows.filter((r) => (!field || r.field === field) && (!q || `${r.chassis_number} ${r.model}`.toLowerCase().includes(q.toLowerCase()))), [res, q, field]);
  const toggle = (ch) => setExclude(exclude.includes(ch) ? exclude.filter((x) => x !== ch) : [...exclude, ch]);
  return (
    <div className="space-y-3" data-testid="run-all-preview">
      <div className="text-sm text-[#73bf44]">{res.checked} vans checked · {res.vans_changing} would change ({res.changes} field changes)</div>
      <div className="flex flex-wrap gap-1.5">
        <button onClick={() => setField("")} className={`text-xs px-2.5 py-1 rounded-full border ${!field ? "border-[#73bf44] text-white" : "border-neutral-800 text-neutral-500"}`}>All</button>
        {Object.entries(res.field_counts).map(([k, n]) => (
          <button key={k} onClick={() => setField(k)} className={`text-xs px-2.5 py-1 rounded-full border ${field === k ? "border-[#73bf44] text-white" : "border-neutral-800 text-neutral-500"}`}>{runFieldLabel(k)} · {n}</button>
        ))}
      </div>
      <label className="flex items-center gap-2 bg-black/40 border border-neutral-800 rounded-lg px-3 py-2"><MagnifyingGlass size={14} className="text-neutral-500" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by chassis or model…" className="bg-transparent outline-none text-sm w-full" data-testid="run-all-search" /></label>
      <div className="max-h-[42vh] overflow-y-auto border border-neutral-800 rounded-xl">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-[#0F0F0F]"><tr className="text-left text-neutral-500 uppercase tracking-wider"><th className="p-2 w-8" /><th className="p-2">Van</th><th className="p-2">Field</th><th className="p-2">Now</th><th className="p-2">After</th></tr></thead>
          <tbody>
            {rows.slice(0, limit).map((r, i) => (
              <tr key={i} className={`border-t border-neutral-800/60 align-top ${exclude.includes(r.chassis_number) ? "opacity-40" : ""}`} data-testid="run-all-row">
                <td className="p-2"><input type="checkbox" checked={!exclude.includes(r.chassis_number)} onChange={() => toggle(r.chassis_number)} className="accent-[#73bf44]" title="Include this van" /></td>
                <td className="p-2"><div className="font-mono text-neutral-200">{r.chassis_number}</div><div className="text-neutral-600">{r.model}</div></td>
                <td className="p-2 text-neutral-400">{runFieldLabel(r.field)}</td>
                <td className="p-2 text-neutral-500 max-w-[220px]">{r.current || "—"}</td>
                <td className="p-2 text-white max-w-[220px]">{r.new}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length > limit && <button onClick={() => setLimit(limit + 300)} className="w-full text-xs text-[#73bf44] py-2">Show more ({rows.length - limit} left)</button>}
      </div>
      {exclude.length > 0 && <div className="text-xs text-amber-300">{exclude.length} van(s) excluded</div>}
    </div>
  );
}

export function RunAllDialog({ open, onClose }) {
  const [mode, setMode] = useState("fill_blanks");
  const [fields, setFields] = useState(RUN_FIELDS.map(([k]) => k));
  const [res, setRes] = useState(null);
  const [exclude, setExclude] = useState([]);
  const [busy, setBusy] = useState(false);
  const call = async (dry) => {
    setBusy(true);
    try {
      const { data } = await adminApi.post("/admin/rulebook/run-all", { mode, fields, exclude, dry_run: dry }, { timeout: 120000 });
      if (dry) setRes(data);
      else { toast.success(`Updated ${data.vans_changing} van(s)`); onClose(); setRes(null); setExclude([]); }
    } catch (e) { toast.error(getErrorMessage(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0F0F0F] border-neutral-800 text-neutral-200 max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="run-all-dialog">
        <DialogHeader><DialogTitle className="font-heading text-white">Run rules on all vans</DialogTitle></DialogHeader>
        <p className="text-sm text-neutral-400 -mt-1">Checks every van on the production schedule and every older van in the Caravan Database. Preview first — nothing is saved until you apply.</p>
        <Options mode={mode} setMode={(m) => { setMode(m); setRes(null); }} fields={fields} setFields={(f) => { setFields(f); setRes(null); }} />
        {res && <Changes res={res} exclude={exclude} setExclude={setExclude} />}
        <div className="flex justify-end gap-2 pt-2">
          <GhostButton onClick={onClose}>Cancel</GhostButton>
          {!res ? (
            <PrimaryButton onClick={() => call(true)} disabled={busy || !fields.length} data-testid="run-all-preview-btn">{busy ? <CircleNotch size={15} className="animate-spin" /> : null} Preview changes</PrimaryButton>
          ) : (
            <PrimaryButton onClick={() => call(false)} disabled={busy || !res.vans_changing} data-testid="run-all-apply"><Lightning size={15} weight="fill" /> Apply to {Math.max(0, res.vans_changing - exclude.length)} van(s)</PrimaryButton>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
